import ReactMarkdown from "react-markdown";
import useDataset from "datasets/hooks/useDataset";
import InitializationStatusSection from "datasets/pages/settings/components/InitializationStatusSection";
import { Loader2 } from "lucide-react";
import remarkGfm from "remark-gfm";
import useSWR from "swr";
import { apiClient, isApiError } from "~/utils/apiClient";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

const fetchMarkdown = async (url: string): Promise<string | null> => {
  try {
    return await apiClient<string>(url, { expectsText: true });
  } catch (e) {
    if (isApiError(e) && e.status === 404) {
      return null;
    }
    throw e;
  }
};

interface OverviewTabProps {
  // Set when rendered outside the dataset route tree (the session context
  // sidebar), which has no :datasetId route param to resolve the dataset from.
  datasetId?: string;
  status: string | undefined;
  statusLoading: boolean;
  generatedAt: string;
  error: string | undefined;
  phase: string | undefined;
  progress: number | undefined;
  onInitialize: () => void;
  onReinitialize: () => void;
  isInitializing?: boolean;
  isReinitializing?: boolean;
  isOwner: boolean;
  // Forces display-only mode regardless of ownership — set by read-only surfaces
  // such as the session context sidebar, where actions live on the full page.
  readOnly?: boolean;
}

const OverviewTab = ({
  datasetId,
  status,
  statusLoading,
  generatedAt,
  error,
  phase,
  progress,
  onInitialize,
  onReinitialize,
  isInitializing,
  isReinitializing,
  isOwner,
  readOnly = false,
}: OverviewTabProps) => {
  const { data: dataset } = useDataset(datasetId);
  const shouldFetch = Boolean(dataset?.id) && status === "completed";
  const { data: markdown, isLoading: markdownLoading } = useSWR<string | null>(
    shouldFetch ? `${VITE_ANALYSIS_AGENT_API}/datasets/${dataset!.id}/agent-markdown` : null,
    fetchMarkdown
  );

  return (
    <div className="flex flex-col gap-4">
      <InitializationStatusSection
        status={status}
        loading={statusLoading}
        generatedAt={generatedAt}
        error={error}
        phase={phase}
        progress={progress}
        onInitialize={onInitialize}
        onReinitialize={onReinitialize}
        isInitializing={isInitializing}
        isReinitializing={isReinitializing}
        isOwner={isOwner && !readOnly}
      />
      {markdownLoading && (
        <div className="flex justify-center py-4">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      )}
      {markdown && !markdownLoading && (
        <div className="max-h-[37.5rem] overflow-y-auto rounded-md border border-border p-4">
          <div className="prose prose-sm max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
